import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FORM_EMAIL } from "@/lib/formConfig";
import { FadeUp } from "@/components/Reveal";
import { Lotus } from "@/components/Motifs";

const inputCls =
  "w-full bg-transparent border-b border-ink/25 focus:border-alta outline-none py-3.5 text-base font-light text-ink placeholder:text-ink/35 transition-colors duration-500";

export default function EnquiryForm({ types, defaultType, testidPrefix = "enquiry" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", enquiry_type: defaultType || types[0], message: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("_subject", `New enquiry — ${form.enquiry_type} — ${form.name}`);
      fd.append("_template", "table");
      fd.append("_captcha", "false");
      if (file) fd.append("attachment", file);
      const res = await fetch(`https://formsubmit.co/ajax/${FORM_EMAIL}`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <motion.div
        data-testid={`${testidPrefix}-success`}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="kantha-border bg-bone p-10 md:p-14 text-center"
      >
        <Lotus className="w-10 h-10 mx-auto text-alta mb-6" />
        <h3 className="font-serif text-3xl md:text-4xl font-light text-ink">Your words are with Ipsita.</h3>
        <p className="mt-5 text-sm md:text-base font-light text-ink/65 max-w-md mx-auto leading-relaxed">
          Thank you for reaching out. Every enquiry is read personally — expect a reply within three working days.
        </p>
        <p className="mt-8 font-bengali text-xl text-terracotta/70">ধন্যবাদ</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="space-y-9">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-9">
        <div>
          <label htmlFor={`${testidPrefix}-name`} className="text-[10px] tracking-[0.25em] uppercase text-ink/50">Name *</label>
          <input id={`${testidPrefix}-name`} data-testid={`${testidPrefix}-name-input`} required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-email`} className="text-[10px] tracking-[0.25em] uppercase text-ink/50">Email *</label>
          <input id={`${testidPrefix}-email`} data-testid={`${testidPrefix}-email-input`} required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-phone`} className="text-[10px] tracking-[0.25em] uppercase text-ink/50">Phone</label>
          <input id={`${testidPrefix}-phone`} data-testid={`${testidPrefix}-phone-input`} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 …" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-city`} className="text-[10px] tracking-[0.25em] uppercase text-ink/50">City</label>
          <input id={`${testidPrefix}-city`} data-testid={`${testidPrefix}-city-input`} value={form.city} onChange={set("city")} className={inputCls} placeholder="Where are you based?" />
        </div>
      </div>

      <div>
        <label htmlFor={`${testidPrefix}-type`} className="text-[10px] tracking-[0.25em] uppercase text-ink/50">Type of Enquiry</label>
        <select
          id={`${testidPrefix}-type`}
          data-testid={`${testidPrefix}-type-select`}
          value={form.enquiry_type}
          onChange={set("enquiry_type")}
          className={`${inputCls} bg-paper`}
        >
          {types.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`${testidPrefix}-message`} className="text-[10px] tracking-[0.25em] uppercase text-ink/50">Message *</label>
        <textarea
          id={`${testidPrefix}-message`}
          data-testid={`${testidPrefix}-message-input`}
          required
          rows={5}
          value={form.message}
          onChange={set("message")}
          className={`${inputCls} resize-none`}
          placeholder="Tell us about the piece, the story, the occasion…"
        />
      </div>

      <div>
        <label className="text-[10px] tracking-[0.25em] uppercase text-ink/50">Reference / Brief (optional)</label>
        <button
          type="button"
          data-testid={`${testidPrefix}-file-button`}
          onClick={() => fileRef.current?.click()}
          className="mt-3 flex items-center gap-3 border border-dashed border-terracotta/40 px-6 py-4 text-sm font-light text-ink/60 hover:border-alta hover:text-ink transition-colors w-full sm:w-auto"
        >
          <span className="text-alta">＋</span> {file ? file.name : "Attach an image, PDF or brief"}
        </button>
        <input
          ref={fileRef}
          type="file"
          data-testid={`${testidPrefix}-file-input`}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </div>

      <FadeUp>
        <button
          type="submit"
          data-testid={`${testidPrefix}-submit-button`}
          disabled={sending}
          className="inline-flex items-center bg-alta text-paper px-10 py-4 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-ink disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send Enquiry"}
        </button>
      </FadeUp>
    </form>
  );
}
